/**
 * React Hook für Google Analytics
 * Bietet eine einfache Schnittstelle für Analytics-Tracking in React-Komponenten
 */

import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import analyticsService, {
  GAEvent,
  InvoiceCreatedEvent,
  SignUpEvent,
  LoginEvent,
  BeginCheckoutEvent
} from '../services/analyticsService';
import logger from '../utils/logger';

interface UseAnalyticsReturn {
  // Status
  isEnabled: boolean;
  isInitialized: boolean;
  pendingEventCount: number;
  
  // Event-Tracking-Methoden
  trackEvent: (event: GAEvent) => void;
  trackInvoiceCreated: (data: Omit<InvoiceCreatedEvent, 'event_name' | 'event_category'>) => void;
  trackSignUp: (data: Omit<SignUpEvent, 'event_name'>) => void;
  trackLogin: (data: Omit<LoginEvent, 'event_name'>) => void;
  trackBeginCheckout: (data: Omit<BeginCheckoutEvent, 'event_name'>) => void;
  
  // Spezielle Tracking-Methoden
  trackButtonClick: (buttonName: string, category?: string) => void;
  trackFormSubmission: (formName: string, success: boolean) => void;
  trackFileDownload: (fileName: string, fileType: string) => void;
  trackError: (errorMessage: string, errorCode?: string) => void;
  
  // Consent Management
  enableAnalytics: () => void;
  disableAnalytics: () => void;
}

interface UseAnalyticsOptions {
  // Automatisches Page View Tracking
  trackPageViews?: boolean;
  // Automatisches Scroll Tracking
  trackScrollDepth?: boolean;
  // Automatisches User Engagement Tracking
  trackUserEngagement?: boolean;
  // Debugging
  debug?: boolean;
}

/**
 * Hook für Google Analytics Integration
 */
export const useAnalytics = (options: UseAnalyticsOptions = {}): UseAnalyticsReturn => {
  const {
    trackPageViews = true,
    trackScrollDepth = false,
    trackUserEngagement = true,
    debug = false
  } = options;

  const location = useLocation();
  const scrollDepthTracked = useRef<Set<number>>(new Set());
  const engagementStartTime = useRef<number>(Date.now());
  const engagementTimer = useRef<NodeJS.Timeout | null>(null);

  // Event-Tracking-Methoden
  const trackEvent = useCallback((event: GAEvent) => {
    analyticsService.trackEvent(event);
    if (debug) {
      logger.debug('Analytics Event getrackt:', event);
    }
  }, [debug]);

  const trackInvoiceCreated = useCallback((data: Omit<InvoiceCreatedEvent, 'event_name' | 'event_category'>) => {
    analyticsService.trackInvoiceCreated(data);
    if (debug) {
      logger.debug('Rechnung erstellt getrackt:', data);
    }
  }, [debug]);

  const trackSignUp = useCallback((data: Omit<SignUpEvent, 'event_name'>) => {
    analyticsService.trackSignUp(data);
    if (debug) {
      logger.debug('Registrierung (sign_up) getrackt:', data);
    }
  }, [debug]);

  const trackLogin = useCallback((data: Omit<LoginEvent, 'event_name'>) => {
    analyticsService.trackLogin(data);
    if (debug) {
      logger.debug('Login getrackt:', data);
    }
  }, [debug]);

  const trackBeginCheckout = useCallback((data: Omit<BeginCheckoutEvent, 'event_name'>) => {
    analyticsService.trackBeginCheckout(data);
    if (debug) {
      logger.debug('Checkout-Start (begin_checkout) getrackt:', data);
    }
  }, [debug]);

  // Spezielle Tracking-Methoden
  const trackButtonClick = useCallback((buttonName: string, category: string = 'button') => {
    trackEvent({
      event_name: 'button_click',
      event_category: category,
      event_label: buttonName,
      custom_parameters: {
        button_name: buttonName,
        page_path: location.pathname
      }
    });
  }, [trackEvent, location.pathname]);

  const trackFormSubmission = useCallback((formName: string, success: boolean) => {
    trackEvent({
      event_name: success ? 'form_submit_success' : 'form_submit_error',
      event_category: 'form',
      event_label: formName,
      custom_parameters: {
        form_name: formName,
        success: success,
        page_path: location.pathname
      }
    });
  }, [trackEvent, location.pathname]);

  const trackFileDownload = useCallback((fileName: string, fileType: string) => {
    trackEvent({
      event_name: 'file_download',
      event_category: 'download',
      event_label: fileName,
      custom_parameters: {
        file_name: fileName,
        file_type: fileType,
        page_path: location.pathname
      }
    });
  }, [trackEvent, location.pathname]);

  const trackError = useCallback((errorMessage: string, errorCode?: string) => {
    trackEvent({
      event_name: 'error_occurred',
      event_category: 'error',
      event_label: errorMessage,
      custom_parameters: {
        error_message: errorMessage,
        error_code: errorCode || 'unknown',
        page_path: location.pathname
      }
    });
  }, [trackEvent, location.pathname]);

  // Consent Management
  const enableAnalytics = useCallback(() => {
    analyticsService.enableAnalytics();
    if (debug) {
      logger.debug('Analytics aktiviert über Hook');
    }
  }, [debug]);

  const disableAnalytics = useCallback(() => {
    analyticsService.disableAnalytics();
    if (debug) {
      logger.debug('Analytics deaktiviert über Hook');
    }
  }, [debug]);

  // Page View Tracking
  useEffect(() => {
    if (!trackPageViews) return;

    const trackPageView = () => {
      analyticsService.trackPageView({
        page_title: document.title,
        page_location: window.location.href,
        page_path: location.pathname
      });

      if (debug) {
        logger.debug('Page View getrackt:', {
          page_title: document.title,
          page_path: location.pathname
        });
      }
    };

    // Kleine Verzögerung, damit der Seitentitel aktualisiert werden kann
    const timer = setTimeout(trackPageView, 100);

    return () => clearTimeout(timer);
  }, [location.pathname, trackPageViews, debug]);

  // Scroll Depth Tracking
  useEffect(() => {
    if (!trackScrollDepth) return;

    const handleScroll = () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );

      // Tracke bei 25%, 50%, 75% und 100%
      const milestones = [25, 50, 75, 100];
      const currentMilestone = milestones.find(
        milestone => scrollPercent >= milestone && !scrollDepthTracked.current.has(milestone)
      );

      if (currentMilestone) {
        scrollDepthTracked.current.add(currentMilestone);
        trackEvent({
          event_name: 'scroll_depth',
          event_category: 'engagement',
          event_label: `${currentMilestone}%`,
          value: currentMilestone,
          custom_parameters: {
            scroll_depth: currentMilestone,
            page_path: location.pathname
          }
        });

        if (debug) {
          logger.debug(`Scroll Depth getrackt: ${currentMilestone}%`);
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      // Reset für neue Seite
      scrollDepthTracked.current.clear();
    };
  }, [location.pathname, trackScrollDepth, trackEvent, debug]);

  // User Engagement Tracking
  useEffect(() => {
    if (!trackUserEngagement) return;

    engagementStartTime.current = Date.now();

    // Tracke Engagement nach 30 Sekunden
    engagementTimer.current = setTimeout(() => {
      const engagementTime = Date.now() - engagementStartTime.current;
      
      trackEvent({
        event_name: 'user_engagement',
        event_category: 'engagement',
        custom_parameters: {
          engagement_time_msec: engagementTime,
          page_path: location.pathname
        }
      });

      if (debug) {
        logger.debug(`User Engagement getrackt: ${Math.round(engagementTime / 1000)}s`);
      }
    }, 30000);

    return () => {
      if (engagementTimer.current) {
        clearTimeout(engagementTimer.current);
        engagementTimer.current = null;
      }
    };
  }, [location.pathname, trackUserEngagement, trackEvent, debug]);

  // Cleanup bei Unmount
  useEffect(() => {
    return () => {
      if (engagementTimer.current) {
        clearTimeout(engagementTimer.current);
      }
    };
  }, []);

  return {
    // Status
    isEnabled: analyticsService.enabled,
    isInitialized: analyticsService.initialized,
    pendingEventCount: analyticsService.pendingEventCount,
    
    // Event-Tracking
    trackEvent,
    trackInvoiceCreated,
    trackSignUp,
    trackLogin,
    trackBeginCheckout,
    
    // Spezielle Tracking-Methoden
    trackButtonClick,
    trackFormSubmission,
    trackFileDownload,
    trackError,
    
    // Consent Management
    enableAnalytics,
    disableAnalytics
  };
};

/**
 * Hook für spezifisches Button-Tracking
 */
export const useButtonTracking = (buttonName: string, category: string = 'button') => {
  const { trackButtonClick } = useAnalytics();

  return useCallback(() => {
    trackButtonClick(buttonName, category);
  }, [trackButtonClick, buttonName, category]);
};

/**
 * Hook für spezifisches Form-Tracking
 */
export const useFormTracking = (formName: string) => {
  const { trackFormSubmission } = useAnalytics();

  const trackSuccess = useCallback(() => {
    trackFormSubmission(formName, true);
  }, [trackFormSubmission, formName]);

  const trackError = useCallback(() => {
    trackFormSubmission(formName, false);
  }, [trackFormSubmission, formName]);

  return { trackSuccess, trackError };
};

export default useAnalytics;


